import { useState } from "react";
import { FormControlLabel, Switch } from "@mui/material";
import { ArtistsBrief } from "../../../api/artists-briefs";
import useBriefImageUpload from "../hooks/useBriefImageUpload";
import useSaveArtistsBriefsMutation, {
  getSaveBriefsErrorMessage,
} from "../hooks/useSaveArtistsBriefsMutation";
import DSBox from "../../system-design/DSBox";
import DSStack from "../../system-design/DSStack";
import DSAlert from "../../system-design/DSAlert";
import DSTextField from "../../system-design/DSTextField";
import DSUploadButton from "../../system-design/DSUploadButton";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";

const AddCardDialog = (props: {
  existingBriefs: ArtistsBrief[];
  onClose: () => void;
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [labels, setLabels] = useState("");
  const [active, setActive] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const { imageUrl, isUploading, uploadError, handleFileChange } =
    useBriefImageUpload();
  // Close only after the save succeeds, so a failed add stays open to retry.
  const {
    mutate: saveBriefs,
    isPending,
    isError: saveError,
    error: saveErrorObj,
  } = useSaveArtistsBriefsMutation({ onSuccess: props.onClose });

  const submit = () => {
    if (!title) {
      setShowWarning(true);
      return;
    }
    const newCard: ArtistsBrief = {
      id: crypto.randomUUID(),
      title,
      description,
      imageUrl,
      labels: labels
        .split(",")
        .map((label) => label.trim())
        .filter(Boolean),
      active,
    };
    saveBriefs([...props.existingBriefs, newCard]);
  };

  return (
    <>
      <DSDialogTitle>Add Card</DSDialogTitle>
      <DSDialogContent>
        {showWarning && (
          <DSAlert severity="error" sx={{ mb: 2 }}>
            Title is required!
          </DSAlert>
        )}
        {uploadError && (
          <DSAlert severity="error" sx={{ mb: 2 }}>
            {uploadError}
          </DSAlert>
        )}
        {saveError && (
          <DSAlert severity="error" sx={{ mb: 2 }}>
            {getSaveBriefsErrorMessage(saveErrorObj)}
          </DSAlert>
        )}
        <DSStack spacing={2} sx={{ mt: 1 }}>
          <DSTextField
            label="Title"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 80 }}
            helperText={`${title.length}/80`}
          />
          <DSTextField
            label="Description"
            multiline
            rows={2}
            fullWidth
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            inputProps={{ maxLength: 500 }}
            helperText={`${description.length}/500`}
          />
          <DSTextField
            label="Labels (comma-separated)"
            placeholder="Flat 2D, Motion, 3D"
            fullWidth
            value={labels}
            onChange={(e) => setLabels(e.target.value)}
          />
          <FormControlLabel
            control={
              <Switch
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
            }
            label="Active (visible on the site)"
          />
          {isUploading ? (
            <DSBox
              sx={{
                width: 120,
                height: 120,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 2,
                bgcolor: "action.hover",
              }}
            >
              <DSCircularProgress />
            </DSBox>
          ) : (
            <label style={{ width: "100%" }}>
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
              <DSUploadButton text={imageUrl ? "Change Image" : "Image"} />
            </label>
          )}
          {!isUploading && imageUrl && (
            <img
              src={imageUrl}
              alt={title}
              style={{
                width: 120,
                height: 120,
                objectFit: "cover",
                borderRadius: 8,
              }}
            />
          )}
        </DSStack>
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={props.onClose}>Cancel</DSSecondaryButton>
        <DSPrimaryButton disabled={isUploading || isPending} onClick={submit}>
          Add Card
        </DSPrimaryButton>
      </DSDialogActions>
    </>
  );
};

export default AddCardDialog;
